import { useEffect } from 'react';
import { useTheme } from '../../hooks/useTheme.jsx';
import { useCanUse } from '../subscription/hooks/useSubscription';
import { deriveSchoolTheme } from './palette.js';
import { getSchoolBrandColours, isBrandingActive } from './brandingModel.js';
import { useBranding } from './BrandingProvider';

/**
 * Applies the active school's white-label theme to the whole app at runtime.
 *
 * Lives inside SchoolProvider (needs the active school) and under ThemeProvider
 * (pushes the derived theme up via setBrandTheme). Renders nothing.
 *
 * Branding applies only when: the school has switched it on, has a valid brand
 * colour, AND the Pro entitlement allows it (customBranding). Otherwise it
 * clears the override and the app falls back to Swan's own light/dark theme —
 * so the Personal workspace and non-Pro schools always look like Swan.
 */
export default function SchoolBrandingController() {
  const canUse = useCanUse('customBranding');
  const { setBrandTheme } = useTheme();

  // Read branding from the shared live provider — the SchoolContext copy is cached
  // and only refreshes on school switch, so it never reflects a fresh branding edit.
  const source = useBranding();

  const colours = getSchoolBrandColours(source);
  const active = canUse && isBrandingActive(source);
  // Depend on the primitive colour values so we only re-derive when they change.
  const key = active ? `${colours.dark}|${colours.accent}|${colours.light}` : '';

  useEffect(() => {
    if (!active) {
      setBrandTheme(null);
      return;
    }
    const derived = deriveSchoolTheme(colours);
    setBrandTheme(derived); // null-safe: deriveSchoolTheme returns null on bad input
  }, [active, key, setBrandTheme]); // eslint-disable-line react-hooks/exhaustive-deps

  // Always clear on unmount (e.g. sign-out) so we never strand a school theme.
  useEffect(() => () => setBrandTheme(null), [setBrandTheme]);

  return null;
}
